import "./ContactMarquee.scss";

import React, {
  CSSProperties,
  FC,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import NavContext from "../Nav/NavProvider";
import { t } from "i18next";
import { getCurrentLanguage } from "../../../i18n";
import { useWindowSize } from "../../../hooks/useWindowSize";
import { YouplanetSections, getSectionSlug } from "../../../constants/general";

interface ContactMarqueeProps {
  alternativeText?: string;
  alternativeSlug?: string;
  isOpened?: boolean;
}

const ContactMarquee: FC<ContactMarqueeProps> = ({
  alternativeText,
  alternativeSlug,
  isOpened
}) => {
  const marquee = useRef<HTMLAnchorElement>();
  const defaultSlug = 'contacto';
  const textContainer = useRef<HTMLParagraphElement>();
  const textToRepeat = useRef<HTMLParagraphElement>();
  const { menuOpened, menuClosed, footerInView } = useContext(NavContext);
  const [repetitions, setRepetitions] = useState<number[]>([]);
  const [contactLink, setContactLink] = useState<string>('');
  const isInContact = alternativeSlug === defaultSlug;
  const translatedText = !!alternativeText
    ? alternativeText
    : t("common.contact");
  const marqueeText = `${translatedText} · `;
  const [isHorizontal, setIsHorizontal] = useState(false);
  const [keyBase, setKeyBase] = useState("");

  const styles = {
    "--time": `${(marqueeText.length / 11) * 3}s`,
  } as CSSProperties;

  const calclutateKeyBase = () => new Date().getTime().toString();

  const calculateRepetitions = () => {
    if (!marquee.current || !textContainer.current || !textToRepeat.current) {
      return;
    }

    setKeyBase(calclutateKeyBase());

    const textContainerCR = textContainer.current.getBoundingClientRect();

    let dimension;
    if (textContainerCR.height > textContainerCR.width) {
      dimension = "height";
      setIsHorizontal(false);
    } else {
      dimension = "width";
      setIsHorizontal(true);
    }

    const textContainerDimension = textContainerCR[dimension];
    const textToRepeatDimension =
      textToRepeat.current.getBoundingClientRect()[dimension];
    const repeat = Math.ceil(textContainerDimension / textToRepeatDimension);

    const numberOfRepetitions = [];
    for (let i = 0; i < repeat + 1; i++) {
      numberOfRepetitions.push(i);
    }

    setRepetitions(numberOfRepetitions);
  };

  const windowSize = useWindowSize();

  useEffect(() => {
    calculateRepetitions();
    setContactLink(getSectionSlug(YouplanetSections.Contact, getCurrentLanguage()));
  }, [windowSize]);

  return (
    <a
      ref={marquee}
      href={!isInContact ? contactLink : undefined}
      className={`contact-marquee ${
        isHorizontal && footerInView ? "contact-marquee--hidden" : ""
      } ${isOpened || menuOpened ? " contact-marquee--opened" : ""}${
        menuClosed ? " contact-marquee--closed" : ""
      } ${isInContact ? 'no-event' : ''}`}
    >
        <span
          ref={textContainer}
          className={`contact-marquee__text ${
            repetitions.length > 0 ? " contact-marquee__text--running" : ""
          }`}
        >
          <span
            key={keyBase}
            ref={textToRepeat}
            className="contact-marquee__repeat"
            style={styles}
          >
            {marqueeText}
          </span>
          {repetitions.map((index) => {
            return (
              <span
                key={`${keyBase}-${index}`}
                className="contact-marquee__repeat"
                style={styles}
              >
                {marqueeText}
              </span>
            );
          })}
        </span>
    </a>
  );
};

export default ContactMarquee;
